<template>
  <div>
    <div class="tw-flex tw-items-end tw-gap-4 tw-mb-6 tw-flex-wrap">
      <CustomSelect
        :options="versions"
        label="Версия сметы 1"
        option-label-key="version_label"
        option-value-key="smeta_version_id"
        @change="onVersion1Change"
        :value="selectedVersion1Id"
        placeholder="Выберите первую версию"
        :clearable="false" style="width: 250px;"
      />
      <CustomSelect
        :options="versions"
        label="Версия сметы 2"
        option-label-key="version_label"
        option-value-key="smeta_version_id"
        @change="onVersion2Change"
        :value="selectedVersion2Id"
        placeholder="Выберите вторую версию"
        :clearable="false" style="width: 250px;"
      />
      <CustomSelect
        :options="entranceOptions"
        label="Подъезд"
        option-label-key="label"
        option-value-key="entrance"
        @change="onEntranceChange"
        :value="selectedEntrance"
        placeholder="Все подъезды"
        :clearable="true"
      />
      <CustomSelect
        :options="floorOptions"
        label="Этаж"
        option-label-key="label"
        option-value-key="level_num"
        @change="onLevelChange"
        :value="selectedLevelNum"
        placeholder="Все этажи"
        :clearable="true"
      />
      <q-btn 
        :disable="!selectedVersion1Id || !selectedVersion2Id || isComparing"
        color="primary" 
        label="Сравнить" 
        @click="compareVersions"
        :loading="isComparing" class="tw-h-[40px] tw-text-white tw-rounded-md tw-capitalize submit-btn"
      />
    </div>

    <div v-if="!selectedVersion1Id || !selectedVersion2Id" 
         style="padding: 40px; background: #f5f5f5; border-radius: 8px; text-align: center;">
      <q-icon name="info" size="48px" color="grey-5" />
      <p style="color: #666; margin: 16px 0 0 0;">
        Выберите две версии сметы для сравнения
      </p>
    </div>

    <div v-else-if="isComparing" style="padding: 40px; text-align: center;">
      <q-spinner color="primary" size="3em" />
      <p style="color: #666; margin-top: 16px;">Загрузка сравнения...</p>
    </div>

    <div v-else-if="hasCompared">
      <div style="margin-bottom: 16px; padding: 16px; background: #e3f2fd; border-radius: 8px; border-left: 4px solid #1976d2;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <div>
            <strong>Версия {{ version1Info?.version_no }}</strong>
            <span v-if="version1Info?.created_at" style="margin-left: 8px; color: #666; font-size: 13px;">
              ({{ version1Info.created_at }})
            </span>
          </div>
          <div style="font-size: 24px; font-weight: 600;">↔</div>
          <div>
            <strong>Версия {{ version2Info?.version_no }}</strong>
            <span v-if="version2Info?.created_at" style="margin-left: 8px; color: #666; font-size: 13px;">
              ({{ version2Info.created_at }})
            </span>
          </div>
        </div>
      </div>

      <CustomTabs
        :tab="activeTab"
        :data="tabs"
        @change_tab="onTabChange"
        alt
        paddingless
      />

      <div style="display: flex; justify-content: flex-end; align-items: center; margin-top: 16px; margin-bottom: 16px;">
        <q-toggle 
          v-model="showOnlyDifferences" 
          label="Показать только изменения"
          dense
        />
      </div>

      <!-- Вкладка "Работы" -->
      <div v-if="activeTab === 'works'">
        <q-table
          v-if="filteredWorkData.length > 0"
          :rows="filteredWorkData"
          :columns="workColumns"
          row-key="work_set_id"
          flat
          bordered
          dense
          :pagination="{rowsPerPage: 0}"
          hide-pagination
        >
          <template v-slot:body="props">
            <q-tr 
              :props="props"
              :style="props.row.is_diff ? 'background: #fff3cd;' : ''"
            >
              <q-td :props="props" key="work_set_name" style="max-width: 300px;">
                <div style="overflow: hidden; text-overflow: ellipsis;">
                  {{ props.row.work_set_name }}
                  <q-tooltip>{{ props.row.work_set_name }}</q-tooltip>
                </div>
              </q-td>

              <q-td :props="props" key="sum_v1" class="text-right">
                <Amount :value="Math.round(props.row.sum_v1 || 0)" /> тг
              </q-td>

              <q-td :props="props" key="sum_v2" class="text-right">
                <span :style="isDifferent(props.row.sum_v1, props.row.sum_v2) ? 'color: #d32f2f; font-weight: 600;' : ''">
                  <Amount :value="Math.round(props.row.sum_v2 || 0)" /> тг
                </span>
              </q-td>

              <q-td :props="props" key="diff" class="text-right">
                <div v-if="props.row.is_diff" style="display: flex; align-items: center; justify-content: flex-end; gap: 4px;">
                  <q-icon 
                    :name="getDiffIcon(props.row.sum_v1, props.row.sum_v2)" 
                    :color="getDiffColor(props.row.sum_v1, props.row.sum_v2)"
                    size="20px"
                  />
                  <span :style="`color: ${getDiffColor(props.row.sum_v1, props.row.sum_v2)}; font-weight: 600;`">
                    <Amount :value="Math.round(Math.abs((props.row.sum_v2 || 0) - (props.row.sum_v1 || 0)))" /> тг
                  </span>
                </div>
                <span v-else style="color: #999; font-style: italic;">Без изменений</span>
              </q-td>
            </q-tr>
          </template>
          
          <template v-slot:bottom-row>
            <q-tr style="background: #f5f5f5; font-weight: 600; border-top: 2px solid #1976d2;">
              <q-td style="text-align: left; padding: 12px;">
                ИТОГО ПО РАБОТАМ:
              </q-td>
              <q-td style="text-align: right; padding: 12px;">
                <Amount :value="Math.round(workTotalV1)" /> тг
              </q-td>
              <q-td style="text-align: right; padding: 12px;">
                <Amount :value="Math.round(workTotalV2)" /> тг
              </q-td>
              <q-td style="text-align: right; padding: 12px;">
                <div style="display: flex; align-items: center; justify-content: flex-end; gap: 4px;">
                  <q-icon 
                    :name="getDiffIcon(workTotalV1, workTotalV2)" 
                    :color="getDiffColor(workTotalV1, workTotalV2)"
                    size="20px"
                  />
                  <span :style="`color: ${getDiffColor(workTotalV1, workTotalV2)}; font-weight: 600;`">
                    <Amount :value="Math.round(Math.abs(workTotalDiff))" /> тг
                  </span>
                </div>
              </q-td>
            </q-tr>
          </template>
        </q-table>
        <div v-else style="padding: 40px; background: #f5f5f5; border-radius: 8px; text-align: center;">
          <q-icon name="check_circle" size="48px" color="green" />
          <p style="color: #666; margin: 16px 0 0 0;">
            Работы в выбранных версиях смет полностью идентичны
          </p>
        </div>
      </div>

      <!-- Вкладка "Материалы" -->
      <div v-if="activeTab === 'materials'">
        <q-table
          v-if="filteredMaterialData.length > 0"
          :rows="filteredMaterialData"
          :columns="materialColumns"
          row-key="material_id"
          flat
          bordered
          dense
          :pagination="{rowsPerPage: 0}"
          hide-pagination
        >
          <template v-slot:body="props">
            <q-tr 
              :props="props"
              :style="props.row.is_diff ? 'background: #fff3cd;' : ''"
            >
              <q-td :props="props" key="material_name" style="max-width: 300px;">
                <div style="overflow: hidden; text-overflow: ellipsis;">
                  {{ props.row.material_name }}
                  <q-tooltip>{{ props.row.material_name }}</q-tooltip>
                </div>
              </q-td>

              <q-td :props="props" key="sum_v1" class="text-right">
                <Amount :value="Math.round(props.row.sum_v1 || 0)" /> тг
              </q-td>

              <q-td :props="props" key="sum_v2" class="text-right">
                <span :style="isDifferent(props.row.sum_v1, props.row.sum_v2) ? 'color: #d32f2f; font-weight: 600;' : ''">
                  <Amount :value="Math.round(props.row.sum_v2 || 0)" /> тг
                </span>
              </q-td>

              <q-td :props="props" key="diff" class="text-right">
                <div v-if="props.row.is_diff" style="display: flex; align-items: center; justify-content: flex-end; gap: 4px;">
                  <q-icon 
                    :name="getDiffIcon(props.row.sum_v1, props.row.sum_v2)" 
                    :color="getDiffColor(props.row.sum_v1, props.row.sum_v2)"
                    size="20px"
                  />
                  <span :style="`color: ${getDiffColor(props.row.sum_v1, props.row.sum_v2)}; font-weight: 600;`">
                    <Amount :value="Math.round(Math.abs((props.row.sum_v2 || 0) - (props.row.sum_v1 || 0)))" /> тг
                  </span>
                </div>
                <span v-else style="color: #999; font-style: italic;">Без изменений</span>
              </q-td>
            </q-tr>
          </template>
          
          <template v-slot:bottom-row>
            <q-tr style="background: #f5f5f5; font-weight: 600; border-top: 2px solid #1976d2;">
              <q-td style="text-align: left; padding: 12px;">
                ИТОГО ПО МАТЕРИАЛАМ:
              </q-td>
              <q-td style="text-align: right; padding: 12px;">
                <Amount :value="Math.round(materialTotalV1)" /> тг
              </q-td>
              <q-td style="text-align: right; padding: 12px;">
                <Amount :value="Math.round(materialTotalV2)" /> тг
              </q-td>
              <q-td style="text-align: right; padding: 12px;">
                <div style="display: flex; align-items: center; justify-content: flex-end; gap: 4px;">
                  <q-icon 
                    :name="getDiffIcon(materialTotalV1, materialTotalV2)" 
                    :color="getDiffColor(materialTotalV1, materialTotalV2)"
                    size="20px"
                  />
                  <span :style="`color: ${getDiffColor(materialTotalV1, materialTotalV2)}; font-weight: 600;`">
                    <Amount :value="Math.round(Math.abs(materialTotalDiff))" /> тг
                  </span>
                </div>
              </q-td>
            </q-tr>
          </template>
        </q-table>
        <div v-else style="padding: 40px; background: #f5f5f5; border-radius: 8px; text-align: center;">
          <q-icon name="check_circle" size="48px" color="green" />
          <p style="color: #666; margin: 16px 0 0 0;">
            Материалы в выбранных версиях смет полностью идентичны
          </p>
        </div>
      </div>

      <!-- Общее итого -->
      <div v-if="hasCompared && (workData.length > 0 || materialData.length > 0)" 
           style="margin-top: 24px; padding: 20px; background: #e3f2fd; border-radius: 8px; border: 2px solid #1976d2;">
        <div style="display: grid; grid-template-columns: 1fr auto auto auto; gap: 16px; align-items: center;">
          <div style="font-size: 18px; font-weight: 700; color: #1976d2;">
            ОБЩЕЕ ИТОГО:
          </div>
          <div style="text-align: right;">
            <div style="font-size: 12px; color: #666; margin-bottom: 4px;">Версия {{ version1Info?.version_no }}</div>
            <div style="font-size: 18px; font-weight: 700;">
              <Amount :value="Math.round(grandTotalV1)" /> тг
            </div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 12px; color: #666; margin-bottom: 4px;">Версия {{ version2Info?.version_no }}</div>
            <div style="font-size: 18px; font-weight: 700;">
              <Amount :value="Math.round(grandTotalV2)" /> тг
            </div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 12px; color: #666; margin-bottom: 4px;">Разница</div>
            <div style="display: flex; align-items: center; justify-content: flex-end; gap: 6px;">
              <q-icon 
                :name="getDiffIcon(grandTotalV1, grandTotalV2)" 
                :color="getDiffColor(grandTotalV1, grandTotalV2)"
                size="24px"
              />
              <span :style="`color: ${getDiffColor(grandTotalV1, grandTotalV2)}; font-weight: 700; font-size: 18px;`">
                <Amount :value="Math.round(Math.abs(grandTotalDiff))" /> тг
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getSmetaVersions, getSmetaDiffWork, getSmetaDiffMaterial, getProjectEntrances, getProjectEntranceFloors } from './services';
import Amount from '@/components/common/Amount.vue';
import CustomSelect from '@/components/form/CustomSelect';
import CustomTabs from '@/components/common/CustomTabs';

export default {
  name: "ProjectSmetaCompare",
  components: { Amount, CustomSelect, CustomTabs },
  props: {
    projectId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      versions: [],
      selectedVersion1Id: null,
      selectedVersion2Id: null,
      isLoadingVersions: false,
      isComparing: false,
      workData: [],
      materialData: [],
      hasCompared: false,
      showOnlyDifferences: false,
      activeTab: 'works',
      tabs: [
        { name: 'works', label: 'Работы' },
        { name: 'materials', label: 'Материалы' }
      ],
      entranceOptions: [],
      floorOptions: [],
      selectedEntrance: null,
      selectedLevelNum: null
    }
  },
  computed: {
    version1Info() {
      return this.versions.find(v => v.smeta_version_id === this.selectedVersion1Id);
    },
    version2Info() {
      return this.versions.find(v => v.smeta_version_id === this.selectedVersion2Id);
    },
    filteredWorkData() {
      if (!this.showOnlyDifferences) {
        return this.workData;
      }
      return this.workData.filter(row => row.is_diff);
    },
    filteredMaterialData() {
      if (!this.showOnlyDifferences) {
        return this.materialData;
      }
      return this.materialData.filter(row => row.is_diff);
    },
    workTotalV1() {
      return this.workData.reduce((sum, row) => sum + (row.sum_v1 || 0), 0);
    },
    workTotalV2() {
      return this.workData.reduce((sum, row) => sum + (row.sum_v2 || 0), 0);
    },
    workTotalDiff() {
      return this.workTotalV2 - this.workTotalV1;
    },
    materialTotalV1() {
      return this.materialData.reduce((sum, row) => sum + (row.sum_v1 || 0), 0);
    },
    materialTotalV2() {
      return this.materialData.reduce((sum, row) => sum + (row.sum_v2 || 0), 0);
    },
    materialTotalDiff() {
      return this.materialTotalV2 - this.materialTotalV1;
    },
    grandTotalV1() {
      return this.workTotalV1 + this.materialTotalV1;
    },
    grandTotalV2() {
      return this.workTotalV2 + this.materialTotalV2;
    },
    grandTotalDiff() {
      return this.grandTotalV2 - this.grandTotalV1;
    },
    workColumns() {
      return [
        { name: 'work_set_name', label: 'Работа', field: 'work_set_name', align: 'left', style: 'max-width: 300px;' },
        { name: 'sum_v1', label: `Сумма (v${this.version1Info?.version_no || '1'})`, field: 'sum_v1', align: 'right' },
        { name: 'sum_v2', label: `Сумма (v${this.version2Info?.version_no || '2'})`, field: 'sum_v2', align: 'right' },
        { name: 'diff', label: 'Разница', field: 'diff', align: 'right' }
      ];
    },
    materialColumns() {
      return [
        { name: 'material_name', label: 'Материал', field: 'material_name', align: 'left', style: 'max-width: 300px;' },
        { name: 'sum_v1', label: `Сумма (v${this.version1Info?.version_no || '1'})`, field: 'sum_v1', align: 'right' },
        { name: 'sum_v2', label: `Сумма (v${this.version2Info?.version_no || '2'})`, field: 'sum_v2', align: 'right' },
        { name: 'diff', label: 'Разница', field: 'diff', align: 'right' }
      ];
    }
  },
  methods: {
    isDifferent(val1, val2) {
      return val1 !== val2;
    },
    getDiffIcon(val1, val2) {
      if (val1 == null || val2 == null) return 'remove';
      const diff = val2 - val1;
      if (diff > 0) return 'arrow_upward';
      if (diff < 0) return 'arrow_downward';
      return 'remove';
    },
    getDiffColor(val1, val2) {
      if (val1 == null || val2 == null) return '#999';
      const diff = val2 - val1;
      if (diff > 0) return '#d32f2f';
      if (diff < 0) return '#388e3c';
      return '#999';
    },
    onTabChange(tab) {
      this.activeTab = tab;
    },
    async loadVersions() {
      this.isLoadingVersions = true;
      const res = await getSmetaVersions(this.projectId);
      this.isLoadingVersions = false;
      
      if (res && res.length > 0) {
        this.versions = res.map(v => ({
          ...v,
          version_label: `v${v.version_no} - ${v.reason_name || 'Без причины'} (${v.created_at})`
        }));
        
        // Автоматически выбрать последние две версии если их больше одной
        if (this.versions.length >= 2) {
          this.selectedVersion1Id = this.versions[1].smeta_version_id;
          this.selectedVersion2Id = this.versions[0].smeta_version_id;
        }
      }
    },
    onVersion1Change(value) {
      this.selectedVersion1Id = value;
      // Сбросить результаты сравнения
      this.workData = [];
      this.materialData = [];
      this.hasCompared = false;
    },
    onVersion2Change(value) {
      this.selectedVersion2Id = value;
      // Сбросить результаты сравнения
      this.workData = [];
      this.materialData = [];
      this.hasCompared = false;
    },
    async onEntranceChange(value) {
      this.selectedEntrance = value || null;
      this.selectedLevelNum = null;
      this.workData = [];
      this.materialData = [];
      this.hasCompared = false;
      if (this.selectedEntrance) {
        const floors = await getProjectEntranceFloors(this.projectId, this.selectedEntrance);
        this.floorOptions = floors || [];
      } else {
        this.floorOptions = [];
      }
      if (this.selectedVersion1Id && this.selectedVersion2Id) {
        await this.compareVersions();
      }
    },
    onLevelChange(value) {
      this.selectedLevelNum = value || null;
      this.workData = [];
      this.materialData = [];
      this.hasCompared = false;
      if (this.selectedVersion1Id && this.selectedVersion2Id) {
        this.compareVersions();
      }
    },
    async compareVersions() {
      if (!this.selectedVersion1Id || !this.selectedVersion2Id) return;
      
      this.isComparing = true;
      
      // Загружаем данные по работам и материалам параллельно
      const [workRes, materialRes] = await Promise.all([
        getSmetaDiffWork(this.projectId, this.selectedVersion1Id, this.selectedVersion2Id, {
          entrance: this.selectedEntrance ?? undefined,
          level_num: this.selectedLevelNum ?? undefined
        }),
        getSmetaDiffMaterial(this.projectId, this.selectedVersion1Id, this.selectedVersion2Id, {
          entrance: this.selectedEntrance ?? undefined,
          level_num: this.selectedLevelNum ?? undefined
        })
      ]);
      
      this.isComparing = false;
      this.hasCompared = true;
      
      this.workData = workRes || [];
      this.materialData = materialRes || [];
    }
  },
  mounted() {
    this.loadVersions();
    (async () => {
      const entrances = await getProjectEntrances(this.projectId);
      this.entranceOptions = entrances || [];
    })();
  }
};
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;
.hover-bg-grey-1:hover {
  background: #f5f5f5;
}
.submit-btn {
  background: $primary-light !important;
}
</style>
